import type { Answers, TSuppressionProfile } from "./_schema";
import { deriveBoostProfile, type ExtraSignals } from "./boost-profile";

/**
 * quiz-trt-switch deriver. Audience: men currently ON TRT. Wraps deriveBoostProfile
 * (cortisol-profile pattern) with:
 *   1. Passthrough of ignite-verbatim ids (concern, energy-3pm,
 *      body-composition, age, stress-symptoms) -> drives the organic Secondary
 *      cause (visceral-adiposity "AROMATASE LOAD" or cortisol-dominance).
 *   2. Severity via extraSignals from the TRT-only questions. On-TRT paths floor
 *      at SUPPRESSED -- the meter reads endogenous production, which exogenous
 *      TRT fully suppresses regardless of duration (operator-approved frame).
 *   3. Conditional forced primary: exogenous-suppression leads on every on-TRT
 *      path; on not-on-trt it is omitted -> organic low-T verdict, no dead-end.
 *
 * Pure function of answers -- reproducible server-side from the persisted
 * submission (rule 11).
 */

const PASSTHROUGH_IDS = ["concern", "energy-3pm", "body-composition", "age", "stress-symptoms"] as const;

function isOnTrt(answers: Answers): boolean {
  const d = answers["trt-duration"];
  return typeof d === "string" && d !== "not-on-trt";
}

function stillTrueCount(answers: Answers): number {
  const v = answers["trt-still-true"];
  return Array.isArray(v) ? v.length : 0;
}

/**
 * On-TRT: tier1=3 floors baseSeverity at "high" (SUPPRESSED) by construction
 * (see deriveBoostProfile.baseSeverity: tier1>=3 -> high). Symptom load +
 * dependency dread add tier2/tier3 for QoL/secondary richness (severity already
 * capped). not-on-trt: no floor -- mild organic signals only, so the rare
 * completer still gets a real read that respects the EARLY-DECLINE floor.
 */
function severitySignals(answers: Answers): ExtraSignals {
  const still = stillTrueCount(answers);
  const forever = answers["trt-forever"];
  const dread = forever === "want-off" || forever === "worried-dependency" ? 1 : 0;
  if (isOnTrt(answers)) {
    return { tier1: 3, tier2: Math.min(still, 2), tier3: dread };
  }
  return { tier2: still >= 2 ? 1 : 0, tier3: dread };
}

function passthrough(answers: Answers): Answers {
  const out: Answers = {};
  for (const id of PASSTHROUGH_IDS) {
    const v = answers[id];
    if (typeof v === "string") out[id] = v;
  }
  return out;
}

export function deriveQuizTrtSwitchProfile(answers: Answers): TSuppressionProfile {
  return deriveBoostProfile(passthrough(answers), {
    extraSignals: severitySignals(answers),
    ...(isOnTrt(answers) ? { forcePrimaryCause: "exogenous-suppression" as const } : {}),
  });
}
